import { ColDef } from "ag-grid-community";
import { IUploaderColumnConfig, MappingOptions, ValidationResultInfo } from "../../types/types";
import AgGridAutocomplete from "../../../../../core/grids/editors/AgGridAutocomplete";

/**
 * Enhanced email validation function
 */
const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== "string") return false;

  const trimmedEmail = email.trim();

  // Check for basic structure: something@something.something
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Additional checks for common issues
  const invalidPatterns = [
    /\.{2,}/, // consecutive dots
    /^\./, // starts with dot
    /\.$/, // ends with dot
    /@\./, // @ followed by dot
    /\.@/, // dot followed by @
    /@.*@/ // multiple @ symbols
  ];

  if (!emailRegex.test(trimmedEmail)) {
    return false;
  }

  // Check for invalid patterns
  for (const pattern of invalidPatterns) {
    if (pattern.test(trimmedEmail)) {
      return false;
    }
  }

  return true;
};

const hasUnsupportedCharacters = (text: string): boolean => {
  if (!text || typeof text !== "string") return false;

  const allowedPattern = /^[a-zA-Z0-9\s.,'`\-_&()]+$/;

  return !allowedPattern.test(text);
};

/**
 * Maps uploader field configurations to AG Grid column definitions
 * @param fields - The uploader column configurations
 * @param options - Additional options for mapping
 * @returns Array of AG Grid column definitions
 */
export function mapFieldsToColDefs(fields: IUploaderColumnConfig[], options: MappingOptions = {}): ColDef[] {
  const { entityRelations = {}, validationResult, editable = true, onCellValueChanged } = options;

  const colDefs: ColDef[] = [
    {
      headerName: "",
      field: "__selection__",
      width: 40,
      pinned: "left",
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true
    },
    {
      headerName: "#",
      field: "__rowNum__",
      width: 80,
      pinned: "left",
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      valueGetter: (params: any) => params.data.id
    },
    {
      headerName: "Errors",
      field: "__errors__",
      width: 200,
      pinned: "left",
      sortable: true,
      filter: true,
      resizable: true,
      autoHeight: true,
      wrapText: true,
      cellClass: "uploader-validation-cell error-cell",
      valueGetter: (params: any) => {
        const rowErrors = validationResult?.errors?.filter(e => e.rowId === params.data.id) || [];
        return rowErrors.map(e => e.message).join("\r\n");
      },
      tooltipValueGetter: (params: any) => {
        const rowErrors = validationResult?.errors?.filter(e => e.rowId === params.data.id) || [];
        return rowErrors.map(e => `${e.field}: ${e.message}`).join("\r\n");
      }
    },
    {
      headerName: "Warnings",
      field: "__warnings__",
      width: 200,
      pinned: "left",
      sortable: true,
      filter: true,
      resizable: true,
      autoHeight: true,
      wrapText: true,
      cellClass: "uploader-validation-cell warning-cell",
      valueGetter: (params: any) => {
        const rowWarnings =
          [...(validationResult?.conflicts || []), ...(validationResult?.duplicates || [])].filter(
            e => e.rowId === params.data.id
          ) || [];
        return rowWarnings.map((e, i) => `${rowWarnings?.length <= 1 ? "" : i + 1 + "."} ${e.message}`).join("\r\n");
      },
      tooltipValueGetter: (params: any) => {
        const rowWarnings =
          [...(validationResult?.conflicts || []), ...(validationResult?.duplicates || [])].filter(
            e => e.rowId === params.data.id
          ) || [];
        return rowWarnings
          .map((e, i) => `${rowWarnings?.length <= 1 ? "" : i + 1 + "."} ${e.field}: ${e.message}`)
          .join("\r\n");
      }
    }
  ];

  fields.forEach(field => {
    const colDef: ColDef = {
      headerName: field.name,
      field: field.key,
      sortable: true,
      filter: true,
      resizable: true,
      editable: editable,
      width: getColumnWidth(field),
      headerTooltip: field.required ? `${field.name} (Required)` : field.name,

      cellClassRules: {
        "cell-error": params => {
          const cellErrors = validationResult?.errors?.some(
            error => error.field === field.key && params.data.id === error.rowId
          );
          return cellErrors;
        },
        "cell-warning": params => {
          const cellWarnings = [...(validationResult?.conflicts || []), ...(validationResult?.duplicates || [])]?.some(
            error => error.field === field.key && params.data.id === error.rowId
          );
          return cellWarnings;
        },
        "cell-entity-relation": () => field.type === "entityRelation"
      },
      ...(field.key === "__rowNum__" || field.key === "__actions__" ? { editable: false } : {}),

      valueFormatter: params => formatCellValue(params, field),
      ...(getCellEditor(field, entityRelations) ? { cellEditor: getCellEditor(field, entityRelations) } : {}),
      ...(getCellEditorParams(field, entityRelations)
        ? { cellEditorParams: getCellEditorParams(field, entityRelations) }
        : {}),
      onCellValueChanged: onCellValueChanged,
      ...getTypeSpecificProps(field, entityRelations, validationResult, onCellValueChanged)
    };

    if (field.required) {
      colDef.headerClass = "required-column-header";
    }

    colDefs.push(colDef);
  });

  return colDefs;
}

/**
 * Get column width based on field type
 */
function getColumnWidth(field: IUploaderColumnConfig): number {
  switch (field.type) {
    case "boolean":
      return 120;
    case "number":
      return 140;
    case "date":
      return 180;
    case "email":
      return 150;
    case "entityRelation":
      return 250;
    case "text":
      return 150;
    default:
      return 200;
  }
}

/**
 * Get type-specific column properties
 */
function getTypeSpecificProps(
  field: IUploaderColumnConfig,
  entityRelations: Record<string, any[]>,
  validationResult?: ValidationResultInfo,
  onCellValueChanged?: (params: any) => void
): Partial<ColDef> {
  switch (field.type) {
    case "boolean":
      return {
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor"
      };

    case "number":
      return {
        filter: "agNumberColumnFilter",
        valueParser: params => {
          const value = Number(params.newValue);
          return isNaN(value) ? null : value;
        }
      };

    case "date":
      return {
        filter: "agDateColumnFilter",
        valueParser: params => {
          // todo
          return params.newValue;
        }
      };

    case "email":
      return {
        filter: "agTextColumnFilter",
        cellClassRules: {
          "cell-invalid-email": (params: any) => {
            if (!params.value) {
              return validationResult?.errors?.some(
                (e: any) =>
                  e.rowId === params.data.id &&
                  e.field === params.colDef.field &&
                  (e.code === "INVALID_EMAIL" || e.code === "MISSING_EMAIL")
              );
            }
            return !isValidEmail(params.value);
          },
          "cell-error": (params: any) => {
            if (!validationResult || !params.data.id || !validationResult.errors) return false;
            const emailErrors = validationResult.errors.filter(
              (e: any) =>
                e.rowId === params.data.id &&
                e.field === params.colDef.field &&
                (e.code === "INVALID_EMAIL" || e.code === "MISSING_EMAIL")
            );
            return emailErrors.length > 0;
          },
          "cell-warning": (params: any) => {
            if (!validationResult || !params.data.id) return false;
            const emailConflicts = [
              ...(validationResult.conflicts || []),
              ...(validationResult.duplicates || [])
            ].filter((e: any) => e.rowId === params.data.id && e.field === params.colDef.field);
            return emailConflicts.length > 0;
          }
        },
        valueParser: (params: any) => {
          if (!params.newValue) return null;
          const email = String(params.newValue).trim().toLowerCase();
          return email;
        },
        valueSetter: (params: any) => {
          const email = params.newValue;
          if (email && !isValidEmail(email)) {
            console.warn(`Invalid email format: ${email}`);
          }
          params.data[params.colDef.field] = email;
          if (onCellValueChanged) {
            onCellValueChanged(params);
          }
          return true;
        },
        cellRenderer: (params: any) => {
          const email = params.value;

          const hasValidationError = validationResult?.errors?.some(
            (e: any) =>
              e.rowId === params.data.id &&
              e.field === params.colDef.field &&
              (e.code === "INVALID_EMAIL" || e.code === "MISSING_EMAIL")
          );

          const hasConflict =
            validationResult &&
            [...(validationResult.conflicts || []), ...(validationResult.duplicates || [])].some(
              (e: any) => e.rowId === params.data.id && e.field === params.colDef.field
            );

          if (!email) {
            if (hasValidationError) {
              return (
                <span style={{ color: "#dc3545" }} title="Email is required">
                  {email || ""}
                </span>
              );
            }
            return "";
          }

          if (!isValidEmail(email)) {
            return (
              <span
                style={{ color: "#dc3545" }}
                title="Invalid email format: Please use format like example@domain.com"
              >
                {email}
              </span>
            );
          }

          if (hasConflict) {
            return (
              <span style={{ color: "#856404" }} title="This email already exists in the database">
                {email}
              </span>
            );
          }

          if (hasValidationError) {
            return (
              <span style={{ color: "#dc3545" }} title="Email validation error">
                {email}
              </span>
            );
          }

          return email;
        },
        cellStyle: (params: any) => {
          const hasValidationError = validationResult?.errors?.some(
            (e: any) =>
              e.rowId === params.data.id &&
              e.field === params.colDef.field &&
              (e.code === "INVALID_EMAIL" || e.code === "MISSING_EMAIL")
          );

          const hasConflict =
            validationResult &&
            [...(validationResult.conflicts || []), ...(validationResult.duplicates || [])].some(
              (e: any) => e.rowId === params.data.id && e.field === params.colDef.field
            );

          // Handle empty email with validation error
          if (!params.value && hasValidationError) {
            return {
              backgroundColor: "#fff2f2",
              borderLeft: "3px solid #dc3545",
              color: "#dc3545"
            };
          }

          if (params.value && !isValidEmail(params.value)) {
            return {
              backgroundColor: "#fff2f2",
              borderLeft: "3px solid #dc3545",
              color: "#dc3545"
            };
          }

          if (hasConflict) {
            return {
              backgroundColor: "#fff3cd",
              borderLeft: "3px solid #ffc107",
              color: "#856404"
            };
          }

          return null;
        }
      };
    case "entityRelation":
      const entities = field.values || entityRelations[field.key] || [];

      const getDisplayName = (entity: any) => {
        if (entity.firstName && entity.lastName) {
          return `${entity.firstName} ${entity.lastName}`;
        }
        return entity.name || entity.value || entity;
      };

      const findEntityByValue = (value: any) => {
        if (!entities || entities.length === 0) return null;

        if (value && typeof value === "object" && value.id) {
          return value;
        }

        if (typeof value === "number") {
          return entities.find(e => e.id === value);
        }

        if (typeof value === "string") {
          const numericValue = parseInt(value, 10);
          if (!isNaN(numericValue)) {
            const entityById = entities.find(e => e.id === numericValue);
            if (entityById) return entityById;
          }

          return entities.find(e => getDisplayName(e) === value);
        }

        return null;
      };

      return {
        cellEditorSelector: () => ({
          component: AgGridAutocomplete,
          popup: true,
          popupPosition: "over"
        }),
        cellEditorPopup: true,
        cellEditorParams: {
          field: [field.key],
          options: entities.map(e => ({
            label: getDisplayName(e),
            id: e.id,
            entity: e
          })),
          getValueLabel: (value: any) => {
            if (!entities || entities.length === 0) return value || "";
            const entity = findEntityByValue(value);
            return entity ? getDisplayName(entity) : value || "";
          },
          allowFreeText: false,
          isPerson: field.key === "stakeholder",
          valueSelector: "label",
          onValueSelected: (selectedValue: string, params: any) => {
            const selectedEntity = entities.find(e => getDisplayName(e) === selectedValue);
            if (selectedEntity && selectedEntity.id) {
              if (params.setValue) {
                params.setValue(selectedEntity.id);
              }
              return selectedEntity.id;
            }
            return selectedValue;
          },
          filterFn: (items: any[], query: string) => {
            const lowerQuery = query.toLowerCase();
            return items.filter((item: any) => {
              const itemName = item.label || getDisplayName(item);
              const lowerName = `${itemName}`.toLowerCase();
              return lowerName.indexOf(lowerQuery) > -1;
            });
          }
        },
        valueParser: (params: any) => {
          // Store only the entity ID
          if (!params.newValue) return null;
          const entity = findEntityByValue(params.newValue);
          const result = entity ? entity.id : params.newValue;
          return result;
        },
        valueFormatter: (params: any) => {
          if (!params.value) return "";

          let valueToFormat = params.value;
          if (typeof params.value === "object" && params.value.id) {
            valueToFormat = params.value.id;
          }

          const entity = findEntityByValue(valueToFormat);
          const result = entity ? getDisplayName(entity) : String(valueToFormat || "");
          return result;
        }
      };

    default:
      return {
        filter: "agTextColumnFilter",
        cellClassRules: {
          "cell-unsupported-chars": (params: any) => {
            if (!params.value) return false;
            return hasUnsupportedCharacters(params.value);
          },
          "cell-error": (params: any) => {
            if (!validationResult || !params.data.id || !validationResult.errors) return false;
            const textErrors = validationResult.errors.filter(
              (e: any) =>
                e.rowId === params.data.id && e.field === params.colDef.field && e.code === "UNSUPPORTED_CHARACTERS"
            );
            return textErrors.length > 0;
          },
          "cell-warning": params => {
            const cellWarnings = [
              ...(validationResult?.conflicts || []),
              ...(validationResult?.duplicates || [])
            ]?.some(error => error.field === field.key && params.data.id === error.rowId);
            return cellWarnings;
          }
        },
        cellStyle: (params: any) => {
          if (!params.value) return null;

          if (hasUnsupportedCharacters(params.value)) {
            return {
              backgroundColor: "#fff2f2",
              borderLeft: "3px solid #dc3545",
              color: "#dc3545"
            };
          }

          return null;
        }
      };
  }
}

function getCellEditor(field: IUploaderColumnConfig, entityRelations: Record<string, any[]>): string | undefined {
  switch (field.type) {
    case "boolean":
      return "agCheckboxCellEditor";
    case "number":
      return "agNumberCellEditor";
    case "date":
      return "agDateCellEditor";
    case "email":
      return "agTextCellEditor";
    case "entityRelation":
      return undefined;
    default:
      return "agTextCellEditor";
  }
}

/**
 * Get cell editor params based on field type
 */
function getCellEditorParams(field: IUploaderColumnConfig, entityRelations: Record<string, any[]>): any {
  if (field.type === "entityRelation") {
    // Cell editor params are handled in getTypeSpecificProps for entity relations
    return undefined;
  }

  if (field.type === "number") {
    return {
      min: 0,
      max: Number.MAX_SAFE_INTEGER,
      precision: 2
    };
  }

  return undefined;
}

/**
 * Format cell value for display
 */
function formatCellValue(params: any, field: IUploaderColumnConfig): string {
  const value = params.value;

  if (value === null || value === undefined || value === "") {
    return field.required ? "(Required)" : "";
  }

  switch (field.type) {
    case "boolean":
      return value ? "Yes" : "No";
    case "date":
      // format date
      return value;
    case "number":
      return Number(value).toLocaleString();
    case "email":
      return String(value);
    default:
      return String(value);
  }
}

/*?*
 * Create a column definition for row selection
 */
export function getSelectionColDef(): ColDef {
  return {
    headerName: "",
    field: "__selection__",
    width: 50,
    pinned: "left",
    sortable: false,
    filter: false,
    resizable: false,
    suppressMenu: true,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true
  };
}

/**
 * Create column definitions with selection
 */
export function mapFieldsToColDefsWithSelection(
  fields: IUploaderColumnConfig[],
  options: MappingOptions = {}
): ColDef[] {
  const colDefs = mapFieldsToColDefs(fields, options);

  colDefs.splice(1, 0, getSelectionColDef());

  return colDefs;
}
